
import React, { useState } from "react";
import { Plus, Edit2, Trash2, FileText, Globe, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableCell, TableBody } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useForm } from "react-hook-form";
import type { Database } from "@/integrations/supabase/types";

type MarketingProject = Database['public']['Tables']['marketing_projects']['Row'];
type MarketingContent = Database['public']['Tables']['marketing_content']['Row'];

interface ContentFormValues {
  name: string;
  url: string;
}

interface ContentListProps {
  selectedProject: MarketingProject | null;
  contents: MarketingContent[];
  onCreateContent: (values: ContentFormValues, file?: File) => Promise<void>;
  onEditContent: (content: MarketingContent, values: ContentFormValues, file?: File) => Promise<void>;
  onDeleteContent: (id: string) => Promise<void>;
}

export const ContentList: React.FC<ContentListProps> = ({
  selectedProject,
  contents,
  onCreateContent,
  onEditContent,
  onDeleteContent
}) => {
  const [isNewContentOpen, setIsNewContentOpen] = useState(false);
  const [isEditContentOpen, setIsEditContentOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<MarketingContent | null>(null);
  const [validationError, setValidationError] = useState<string>('');
  const [editValidationError, setEditValidationError] = useState<string>('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const editFileInputRef = React.useRef<HTMLInputElement>(null);
  const contentTableRef = React.useRef<HTMLDivElement>(null);

  const contentForm = useForm<ContentFormValues>({
    defaultValues: {
      name: '',
      url: ''
    }
  });

  const editContentForm = useForm<ContentFormValues>({
    defaultValues: {
      name: '',
      url: ''
    }
  });

  const handleCreateContent = async (values: ContentFormValues) => {
    if (!values.name.trim() || !values.url.trim()) {
      setValidationError('Both Content Name and URL or File Path are required');
      return;
    }

    const file = fileInputRef.current?.files?.[0];
    await onCreateContent(values, file);
    setIsNewContentOpen(false);
    setValidationError('');
    contentForm.reset();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEditContent = async (values: ContentFormValues) => {
    if (!editingContent) return;
    
    if (!values.name.trim() || !values.url.trim()) {
      setEditValidationError('Both Content Name and URL or File Path are required');
      return;
    }

    const file = editFileInputRef.current?.files?.[0];
    await onEditContent(editingContent, values, file);
    setIsEditContentOpen(false);
    setEditingContent(null);
    setEditValidationError('');
    editContentForm.reset();
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
  };

  const generateEmptyRows = (count: number, columnCount: number, startIndex: number) => {
    return Array(count).fill(null).map((_, index) => (
      <TableRow key={`empty-${index}`}>
        <TableCell className="text-[11px] text-center text-white">{startIndex + index + 1}</TableCell>
        {Array(columnCount - 2).fill(null).map((_, colIndex) => (
          <TableCell key={`empty-${index}-${colIndex}`} className="text-[11px] text-white">-</TableCell>
        ))}
        <TableCell className="text-right text-white">-</TableCell>
      </TableRow>
    ));
  };

  const calculateEmptyRowCount = (existingRowCount: number): number => {
    const maxVisibleRows = 4;
    return Math.max(maxVisibleRows - existingRowCount, 0);
  };

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 h-[265px] py-[21px]">
      <div className="flex justify-between items-center mb-2 mx-[10px]">
        <h2 className="font-semibold text-base text-white">Contents</h2>
        <Dialog open={isNewContentOpen} onOpenChange={setIsNewContentOpen}>
          <DialogTrigger asChild>
            <Button size="sm" disabled={!selectedProject} className="bg-primary hover:bg-primary/90 text-slate-950 text-xs font-normal">
              <Plus className="w-4 h-4 mr-2" />
              New Content
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Content</DialogTitle>
            </DialogHeader>
            <Form {...contentForm}>
              <form onSubmit={contentForm.handleSubmit(handleCreateContent)} className="space-y-4">
                <FormField
                  control={contentForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content Name</FormLabel>
                      <FormControl>
                        <Input {...field} className="border-primary/20" />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={contentForm.control}
                  name="url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>URL or File Path</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input {...field} className="border-primary/20 pr-10" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <Upload className="h-4 w-4 text-primary" />
                          </Button>
                          <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                field.onChange(file.name);
                                contentForm.setValue("name", file.name.split(".")[0]);
                              }
                            }}
                          />
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                {validationError && (
                  <p className="text-sm text-destructive">{validationError}</p>
                )}
                <div className="flex justify-start">
                  <Button type="submit" className="inline-flex bg-primary hover:bg-primary/90 text-black">
                    Add Content
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
      <div ref={contentTableRef} className="overflow-auto rounded-md border border-primary/20 h-[calc(100%-40px)] px-[11px]">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-primary/5">
              <TableHead className="text-[11px] text-white font-semibold text-center">SN</TableHead>
              <TableHead className="text-[11px] text-white font-semibold">Name</TableHead>
              <TableHead className="text-[11px] text-white font-semibold">Type</TableHead>
              <TableHead className="text-[11px] text-white font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contents.map((content, index) => (
              <TableRow key={content.id} className="hover:bg-primary/5">
                <TableCell className="text-[11px] text-white text-center">{index + 1}</TableCell>
                <TableCell className="text-[11px] text-white">
                  {content.name}
                </TableCell>
                <TableCell className="text-[11px] text-white">
                  {content.type === "file" ? (
                    <FileText className="h-3 w-3" />
                  ) : (
                    <Globe className="h-3 w-3" />
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 hover:bg-primary/10 text-primary-light"
                      onClick={() => {
                        setEditingContent(content);
                        editContentForm.setValue("name", content.name);
                        editContentForm.setValue("url", content.content_url);
                        setIsEditContentOpen(true);
                      }}
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive text-primary-light"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-black">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-[#ea384c]">Delete Content</AlertDialogTitle>
                          <AlertDialogDescription className="text-white">
                            Are you sure you want to delete this content? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeleteContent(content.id)}
                            className="bg-primary"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {generateEmptyRows(calculateEmptyRowCount(contents.length), 4, contents.length)}
          </TableBody>
        </Table>
      </div>

      {/* Edit Content dialog */}
      <Dialog open={isEditContentOpen} onOpenChange={setIsEditContentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
          </DialogHeader>
          <Form {...editContentForm}>
            <form onSubmit={editContentForm.handleSubmit(handleEditContent)} className="space-y-4">
              <FormField
                control={editContentForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content Name</FormLabel>
                    <FormControl>
                      <Input {...field} className="border-primary/20" />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={editContentForm.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL or File Path</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input {...field} className="border-primary/20 pr-10" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                          onClick={() => editFileInputRef.current?.click()}
                        >
                          <Upload className="h-4 w-4 text-primary" />
                        </Button>
                        <input
                          type="file"
                          ref={editFileInputRef}
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              field.onChange(file.name);
                              editContentForm.setValue("name", file.name.split(".")[0]);
                            }
                          }}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              {editValidationError && (
                <p className="text-sm text-destructive">{editValidationError}</p>
              )}
              <div className="flex justify-start">
                <Button type="submit" className="inline-flex bg-primary hover:bg-primary/90 text-black">
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
